import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { Settings, Clock, Calendar, Save, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AttendanceSettings = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [settings, setSettings] = useState(null);

  const defaultDayTimings = [
    { day: 'Monday', check_in_start: '00:00', check_in_end: '23:59', check_out_start: '00:00', check_out_end: '23:59', is_working_day: true },
    { day: 'Tuesday', check_in_start: '00:00', check_in_end: '23:59', check_out_start: '00:00', check_out_end: '23:59', is_working_day: true },
    { day: 'Wednesday', check_in_start: '00:00', check_in_end: '23:59', check_out_start: '00:00', check_out_end: '23:59', is_working_day: true },
    { day: 'Thursday', check_in_start: '00:00', check_in_end: '23:59', check_out_start: '00:00', check_out_end: '23:59', is_working_day: true },
    { day: 'Friday', check_in_start: '00:00', check_in_end: '23:59', check_out_start: '00:00', check_out_end: '23:59', is_working_day: true },
    { day: 'Saturday', check_in_start: '00:00', check_in_end: '23:59', check_out_start: '00:00', check_out_end: '23:59', is_working_day: true },
    { day: 'Sunday', check_in_start: '00:00', check_in_end: '00:00', check_out_start: '00:00', check_out_end: '00:00', is_working_day: false },
  ];

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/attendance/settings?month=${selectedMonth}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings({
          month: selectedMonth,
          total_working_days: data.total_working_days || 22,
          standard_working_hours: data.standard_working_hours || 8.0,
          overtime_threshold: data.overtime_threshold || 8.0,
          allow_all_day: data.allow_all_day || false,
          day_wise_timings: data.day_wise_timings || defaultDayTimings
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [token, selectedMonth]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(
        `${API_URL}/api/attendance/settings?month=${settings.month}&total_working_days=${settings.total_working_days}&standard_working_hours=${settings.standard_working_hours}&overtime_threshold=${settings.overtime_threshold}&allow_all_day=${settings.allow_all_day}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(settings.day_wise_timings)
        }
      );

      if (response.ok) {
        toast.success('Settings saved successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save settings');
      }
    } catch (error) {
      toast.error('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  const updateDayTiming = (dayIndex, field, value) => {
    const newTimings = [...settings.day_wise_timings];
    newTimings[dayIndex] = { ...newTimings[dayIndex], [field]: value };
    setSettings({ ...settings, day_wise_timings: newTimings });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Attendance Settings</h1>
          <p className="text-gray-600">Configure working days, hours, and check-in/out timings</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Label>Month:</Label>
            <Input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="w-40"
            />
          </div>
          <Button onClick={fetchSettings} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Load
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Settings
          </Button>
        </div>
      </div>

      {settings && (
        <>
          {/* General Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                General Settings for {settings.month}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div>
                  <Label htmlFor="working_days">Total Working Days</Label>
                  <Input
                    id="working_days"
                    type="number"
                    value={settings.total_working_days}
                    onChange={(e) => setSettings({ ...settings, total_working_days: parseInt(e.target.value) || 0 })}
                    min={1}
                    max={31}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Number of working days in this month</p>
                </div>
                <div>
                  <Label htmlFor="standard_hours">Standard Working Hours</Label>
                  <Input
                    id="standard_hours"
                    type="number"
                    step="0.5"
                    value={settings.standard_working_hours}
                    onChange={(e) => setSettings({ ...settings, standard_working_hours: parseFloat(e.target.value) || 8 })}
                    min={1}
                    max={24}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Expected hours per day</p>
                </div>
                <div>
                  <Label htmlFor="overtime_threshold">Overtime Threshold (hours)</Label>
                  <Input
                    id="overtime_threshold"
                    type="number"
                    step="0.5"
                    value={settings.overtime_threshold}
                    onChange={(e) => setSettings({ ...settings, overtime_threshold: parseFloat(e.target.value) || 8 })}
                    min={1}
                    max={24}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Hours after which overtime is calculated</p>
                </div>
                <div>
                  <Label htmlFor="allow_all_day" className="text-base">Allow All Day Check-in</Label>
                  <div className="flex items-center gap-3 mt-2">
                    <Switch
                      id="allow_all_day"
                      checked={settings.allow_all_day}
                      onCheckedChange={(checked) => setSettings({ ...settings, allow_all_day: checked })}
                    />
                    <span className={`text-sm ${settings.allow_all_day ? 'text-green-600 font-medium' : 'text-gray-500'}`}>
                      {settings.allow_all_day ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {settings.allow_all_day 
                      ? 'Check-in/out allowed any time (unusual times will be flagged)'
                      : 'Strict time restrictions enforced'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Day-wise Timings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Day-wise Check-in/Check-out Timings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Day</TableHead>
                    <TableHead>Working Day</TableHead>
                    <TableHead>Check-in Start</TableHead>
                    <TableHead>Check-in End</TableHead>
                    <TableHead>Check-out Start</TableHead>
                    <TableHead>Check-out End</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {settings.day_wise_timings.map((day, index) => (
                    <TableRow key={day.day} className={!day.is_working_day ? 'bg-gray-50' : ''}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          {day.day}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={day.is_working_day}
                          onCheckedChange={(checked) => updateDayTiming(index, 'is_working_day', checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="time"
                          value={day.check_in_start}
                          onChange={(e) => updateDayTiming(index, 'check_in_start', e.target.value)}
                          disabled={!day.is_working_day}
                          className="w-28"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="time"
                          value={day.check_in_end}
                          onChange={(e) => updateDayTiming(index, 'check_in_end', e.target.value)}
                          disabled={!day.is_working_day}
                          className="w-28"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="time"
                          value={day.check_out_start}
                          onChange={(e) => updateDayTiming(index, 'check_out_start', e.target.value)}
                          disabled={!day.is_working_day}
                          className="w-28"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="time"
                          value={day.check_out_end}
                          onChange={(e) => updateDayTiming(index, 'check_out_end', e.target.value)}
                          disabled={!day.is_working_day}
                          className="w-28"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="text-sm text-gray-500 mt-4">
                <strong>Note:</strong> Check-in is only allowed between Check-in Start and Check-in End times. 
                Late check-outs (after 11:55 PM) are flagged and Admin is notified.
              </p>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default AttendanceSettings;
